import React, { useState, useRef } from 'react';

// Icônes
import { BarChart2, AlertCircle, Clock, LineChart } from 'lucide-react';

// Librairies externes
import { useTranslation } from 'react-i18next';

// Stores et services
import { useAppSelector } from '../../../stores';
import { FamlaServices } from '../../../services';
import { CompanyAnalysisService } from '../../../services/api/companyAnalysisService';

// Types
import { AnalysisPanelProps } from './types';
import { Report } from '../../lib/types';

// Hooks personnalisés
import { useReports } from './hooks/useReports';
import { useFeedback } from './hooks/useFeedback';
import { useScrollToBottom } from './hooks/useScrollToBottom';
import { useFileUpload } from "./hooks/useFileUpload"

// Composants internes
import { Header } from './components/Header';
import { PromptInput } from './components/PromptInput';
import { ReportList } from './components/ReportList';
import { PromptSuggestions } from './components/PromptSuggestions';
import { ReportsModal } from './components/ReportModal';
import SelectedItemsDisplay from "./components/SelectedItemsDisplay"
import { SaveAnalysisModal } from './components/saveModal';
import InsufficientCreditsModal from '../../../views/modal/InsufficientCreditsModal';
import { trackUiButtonEventAsync, UI_BUTTON_EVENTS } from '../../../services/api/uiButtonStatsService';
// import { SaveAnalysisModal } from './components/saveModal'; // Décommenter si nécessaire


export function AnalysisPanel(props: AnalysisPanelProps) {

    // == Props ==
    const { processId, mapId, setShoRightPanel, isMaximized, onToggleMaximize, AnalyseId } = props;

    // == Sélecteurs ==
    const users = useAppSelector((state) => state.auth);
    const company = useAppSelector((state) => state.company.company);
    const role = useAppSelector((state) => state.role.role);
    
    // Déterminer si l'utilisateur actuel est le propriétaire de la compagnie ou un admin
    const isCompanyOwner = Boolean(
        company?.owner?._id && 
        users?.user?._id && 
        (company.owner._id === users.user._id || 
         company.owner._id?.toString() === users.user._id?.toString())
    );
    const isAdmin = role === "ADMIN" || role === "admin";
    const isOwner = isCompanyOwner || isAdmin;
    // Récupérer l'email du propriétaire de la company pour les utilisateurs non-admin
    const [ownerEmail, setOwnerEmail] = React.useState<string | undefined>(undefined);
    
    // Récupérer l'email du propriétaire via l'API
    React.useEffect(() => {
        const loadOwnerEmail = async () => {
            if (isAdmin || !users.session?.company_id || !users.access_token) return;
            
            try {
                const data = await FamlaServices("api_host", `/company/by/id/${users.session.company_id}`, "GET", "", users.access_token);
                
                if (data.status === 200 && data.body?.data?.owner?.email) {
                    setOwnerEmail(data.body.data.owner.email);
                }
            } catch (error) {
                // Erreur silencieuse
            }
        };
        
        loadOwnerEmail();
    }, [isAdmin, users.session?.company_id, users.access_token]);

    // == Traduction ==
    const { t } = useTranslation();

    // == Refs ==
    const reportEndRef = useRef<HTMLDivElement | null>(null);
    const insufficientCreditsModalRef = useRef<HTMLSpanElement>(null);
    const submitFromSuggestionRef = useRef(false);

    // == États ==
    const [prompt, setPrompt] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
    const [isDisableInput, setIsDisableInput] = useState(false);
    const [isAnalyseSaved, setIsAnalyseSaved] = useState(false);
    const [isReportsModalOpen, setIsReportsModalOpen] = useState(false);
    const [reportAnalyseId, setReportAnalyseId] = useState<string>("");
    const [swimLane, setSwimlane] = useState<any[]>([]);
    const [insufficientCreditsInfo, setInsufficientCreditsInfo] = useState<{
        creditsRequired: number;
        creditsAvailable: number;
        deficit: number;
        plan?: string;
    } | null>(null);


    // == Hooks personnalisés ==
    const { report, setReport, isLoadingReports } = useReports(mapId, users, AnalyseId);
    const { feedback, setFeedback, handleFeedbackClick, handleCommentSubmit } = useFeedback(users);
    useScrollToBottom(reportEndRef, { reports: report, feedback });
    const fileUploadProps = useFileUpload();


    // == Effets ==
    React.useEffect(() => {
        if (report.length > 0) {
            setReportAnalyseId(report[0]?.analysis_id ?? "");
        }

        const lastItem = report[report.length - 1];

        if (!lastItem || !Array.isArray(lastItem.step)) {
            setIsDisableInput(false);
            return;
        }

        const [stepMain, stepSub] = lastItem.step;

        const shouldDisable =
            (stepMain === 1 && stepSub === 2) ||
            (stepMain === 1 && stepSub === 3);

        setIsDisableInput(shouldDisable);
    }, [report]);


    React.useEffect(() => {

        const loadSwimlaneState = async () => {
            try {
                const response = await FamlaServices("api_host", `/mapping/process/${processId}`, "GET", "", users.access_token);

                if (response.status === 200) {
                    const newData = response.body.data;
                    setSwimlane(newData)
                } else {
                    return null;
                }
            } catch (error) {
                return null;
            }
        };
        loadSwimlaneState()


    }, [processId]);

    // == Ouvrir le modal de crédits insuffisants quand nécessaire ==
    React.useEffect(() => {
        if (insufficientCreditsInfo) {
            console.log('[AnalysisPanel] Opening modal with credits info:', insufficientCreditsInfo);
            // Attendre que le modal soit monté dans le DOM
            setTimeout(() => {
                const modalElement = document.getElementById('insufficientCreditsModalAnalysisPanel');
                console.log('[AnalysisPanel] Modal element found:', modalElement);
                if (modalElement) {
                    // Utiliser Bootstrap Modal API via window (Bootstrap chargé globalement)
                    const bootstrap = (window as any).bootstrap;
                    if (bootstrap && bootstrap.Modal) {
                        const modal = new bootstrap.Modal(modalElement, {
                            backdrop: true,
                            keyboard: true,
                            focus: true
                        });
                        modal.show();
                        console.log('[AnalysisPanel] Modal shown via Bootstrap API');
                        
                        // S'assurer que le modal est visible et au-dessus du backdrop
                        setTimeout(() => {
                            modalElement.style.display = 'block';
                            modalElement.classList.add('show');
                            modalElement.style.zIndex = '10000';
                            modalElement.style.position = 'fixed';
                            
                            // Trouver le backdrop créé par Bootstrap et ajuster son z-index
                            const backdrop = document.querySelector('.modal-backdrop');
                            if (backdrop) {
                                (backdrop as HTMLElement).style.zIndex = '9999';
                                (backdrop as HTMLElement).style.pointerEvents = 'auto';
                            }
                            
                            // S'assurer que tous les éléments du modal sont cliquables
                            const modalDialog = modalElement.querySelector('.modal-dialog');
                            const modalContent = modalElement.querySelector('.modal-content');
                            if (modalDialog) {
                                (modalDialog as HTMLElement).style.zIndex = '10001';
                                (modalDialog as HTMLElement).style.pointerEvents = 'auto';
                                (modalDialog as HTMLElement).style.position = 'relative';
                            }
                            if (modalContent) {
                                (modalContent as HTMLElement).style.zIndex = '10002';
                                (modalContent as HTMLElement).style.pointerEvents = 'auto';
                                (modalContent as HTMLElement).style.position = 'relative';
                            }
                            
                            // Forcer le modal à être au-dessus de tout
                            document.body.style.overflow = 'hidden';
                        }, 50);
                    } else if (insufficientCreditsModalRef.current) {
                        // Fallback : utiliser le span trigger
                        insufficientCreditsModalRef.current.click();
                        console.log('[AnalysisPanel] Modal triggered via span click');
                    }
                } else if (insufficientCreditsModalRef.current) {
                    // Fallback : utiliser le span trigger
                    insufficientCreditsModalRef.current.click();
                    console.log('[AnalysisPanel] Modal triggered via span click (fallback)');
                }
            }, 200); // Augmenter le délai pour s'assurer que le modal est monté
        }
    }, [insufficientCreditsInfo]);

    // == Fonctions ==

    async function uploadFileToAnalyze() {
        const file = fileUploadProps.selectedFile
        try {
            if (!file) {
                return;
            }
            if (file.type) {
                const endpoint = `/file/new/upload/analyze?process_id=${processId}&analyse_id=DEFAULT`;
                const formData = new FormData();
                file && formData.append("fileobject", file);

                const data = await FamlaServices("api_host", endpoint, "POST", formData, "");

                if (data.status === 201) {
                    fileUploadProps.clearSelection();
                }
            } else {
                // const endpoint = `/file/?interview_id=${""}`;
                // const formData = new FormData();
                // file && formData.append("fileobject", file);

                // const data = await FamlaServices("api_host", endpoint, "POST", formData, "");

                // if (data.status === 201) {
                // }
            }
        } catch (error) {

        }
    }
    const handleSubmit = async (promptText: string) => {
        if (!promptText.trim()) return;

        if (submitFromSuggestionRef.current) {
            submitFromSuggestionRef.current = false;
        } else {
            void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.analysisPromptSubmit);
        }

        const generateTempId = () => `temp-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
        const tempId = generateTempId();
        const wasEmpty = report.length === 0;

        setReport((prev) => [
            ...prev,
            {
                _id: tempId,
                process_id: processId,
                user_id: "",
                map_id: mapId,
                prompt: promptText,
                text: "",
                comment: "",
                created_at: new Date().toISOString(),
                updated_at: "",
                name: "",
                analysis_id: "",
                step: [],
                template: "",
                thumbs: ""
            }
        ]);

        setIsLoading(true);

        if (fileUploadProps.selectedFile) {
            await uploadFileToAnalyze();

        }

        const analyseData = {
            text: promptText,
            map_list: fileUploadProps.selectedMapIds || []
        }

        try {
            const endpoint = `/report/answer/process/${processId}/${mapId}?analyse_id=${reportAnalyseId}`;
            const response = await FamlaServices("api_host", endpoint, "POST", analyseData, users.access_token);
            const newReports = response.body.data;

            if (response.status === 200) {
                setReport((prev) => {
                    const filtered = prev.filter((r) => r._id !== tempId);
                    const normalized = Array.isArray(newReports) ? newReports : [newReports];
                    return [...filtered, ...normalized];
                });

                // ✅ Récupérer l'analysis_id du premier rapport (ou mise à jour si vide)
                if (wasEmpty || !reportAnalyseId) {
                    const firstId = Array.isArray(newReports) ? newReports[0]?.analysis_id : newReports.analysis_id;
                    if (firstId) {
                        setReportAnalyseId(firstId);
                    }
                }

                setPrompt('');
            } else if (response.status === 402 || response.status === 403) {
                // [AI CREDITS] Gérer les cas 402 Payment Required et 403 Forbidden (crédits insuffisants)
                setReport((prev) => prev.filter((r) => r._id !== tempId));
                
                console.log('[AnalysisPanel] 402/403 Response:', {
                    status: response.status,
                    body: response.body,
                    data: newReports
                });
                
                // Si response.body.data contient les données de crédits (format 402)
                if (newReports && typeof newReports === 'object' && 'credits_required' in newReports) {
                    const creditsData = newReports as {
                        credits_required: number;
                        credits_available: number;
                        deficit: number;
                        plan?: string;
                    };
                    console.log('[AnalysisPanel] Credits data found:', creditsData);
                    setInsufficientCreditsInfo({
                        creditsRequired: creditsData.credits_required || 0,
                        creditsAvailable: creditsData.credits_available || 0,
                        deficit: creditsData.deficit || 0,
                        plan: creditsData.plan || "FREE"
                    });
                } else {
                    // Format 403 (erreur simple) - afficher modal avec valeurs par défaut
                    console.log('[AnalysisPanel] No credits data, using defaults');
                    setInsufficientCreditsInfo({
                        creditsRequired: 0,
                        creditsAvailable: 0,
                        deficit: 0,
                        plan: "FREE"
                    });
                }
            }
        } catch (error) {
            setReport((prev) => prev.filter((r) => r._id !== tempId));
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyText = async (text: string, reportId: string) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error('Failed to copy text:', err);
        }
    };

    const handleSaveReport = async (reportId: string, name: string): Promise<boolean> => {
        await CompanyAnalysisService.saveReportAnalysis(reportId, name, users.access_token);
        setIsAnalyseSaved(true);
        window.dispatchEvent(new CustomEvent('analysisListUpdated'));
        return true;
    };

    const handleDeleteAnalysis = () => {
        setReport([]);
        setReportAnalyseId('')
    };

    const handleNewAnalysis = () => {
        if (report.length > 0) {
            setIsSaveModalOpen(true);
        }
    };




    const handleSaveAndNew = async (name: string): Promise<boolean> => {
        if (report.length === 0) {
            return false;
        }

        const analysisId = reportAnalyseId || report[0].analysis_id;
        if (!analysisId) {
            console.error('No analysis_id found in reports');
            return false;
        }

        await CompanyAnalysisService.saveAnalysis(analysisId, name, users.access_token);
        void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.analysisSaveAllConfirm);
        setIsAnalyseSaved(true);
        setReport([]);
        setFeedback({});
        window.dispatchEvent(new CustomEvent('analysisListUpdated'));
        return true;
    };



    const handleReportSelect = async (savedReport: Report) => {
        setIsReportsModalOpen(false);
        
        // Charger TOUS les rapports de l'analyse (pas seulement celui cliqué)
        try {
            const response = await FamlaServices(
                "api_host",
                `/report/by/analysis/${savedReport.analysis_id}`,
                "GET",
                "",
                users.access_token
            );
            
            if (response.status === 200) {
                const allReports = response.body.data;
                // Trier par date de création (chronologique)
                const sortedReports = Array.isArray(allReports)
                    ? allReports.sort((a: Report, b: Report) => 
                        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                      )
                    : [allReports];
                
                setReport(sortedReports);
                
                // Mettre à jour reportAnalyseId pour les prochains rapports
                if (sortedReports.length > 0 && sortedReports[0].analysis_id) {
                    setReportAnalyseId(sortedReports[0].analysis_id);
                }
            } else {
                // Fallback : afficher au moins le rapport cliqué
                setReport([savedReport]);
            }
        } catch (error) {
            console.error('❌ Erreur lors du chargement de l\'analyse:', error);
            // Fallback : afficher au moins le rapport cliqué
            setReport([savedReport]);
        }
    };

    // == Suggestions de prompt ==
    const promptSuggestions = [
        {
            icon: <BarChart2 size={16} />,
            title: t('features.swimlane.summaryStatistics'),
            description: t('features.swimlane.analyzeAllAttributesData'),
            prompt: t('features.swimlane.prompt1')
        },
        {
            icon: <AlertCircle size={16} />,
            title: t('features.swimlane.bottleneckIdentification'),
            description: t('features.swimlane.identifyStakeholders'),
            prompt: t('features.swimlane.prompt2'),
        },
        {
            icon: <Clock size={16} />,
            title: t('features.swimlane.leadTimeAnalysis'),
            description: t('features.swimlane.analyzeLead'),
            prompt: t('features.swimlane.prompt3'),
        },
        {
            icon: <LineChart size={16} />,
            title: t('features.swimlane.qualityAnalysis'),
            description: t('features.swimlane.examineDefect'),
            prompt: t('features.swimlane.prompt4')
        }
    ];



    return (
        <>
            {/* <Toaster /> */}
            <Header
                userName={users.user.fullname}
                setShoRightPanel={setShoRightPanel}
                onDeleteAnalysis={handleDeleteAnalysis}
                onNewAnalysis={handleNewAnalysis}
                onOpenReports={() => setIsReportsModalOpen(true)}
                onToggleMaximize={onToggleMaximize}
                isMaximized={isMaximized}
            />

            <div className="flex-1 p-1 pb-4 mb-14 space-y-6 overflow-auto relative">
                {isLoadingReports ? (
                    <ReportList
                        reports={report}
                        isLoading={isLoadingReports}
                        feedback={feedback}
                        onFeedbackClick={handleFeedbackClick}
                        onCommentSubmit={handleCommentSubmit}
                        onCopyText={handleCopyText}
                        onSaveReport={handleSaveReport}
                        setFeedback={setFeedback}
                        onResponse={handleSubmit}
                        setReport={setReport}
                    />
                ) : report && report.length > 0 ? (
                    <ReportList
                        reports={report}
                        isLoading={isLoadingReports}
                        feedback={feedback}
                        onFeedbackClick={handleFeedbackClick}
                        onCommentSubmit={handleCommentSubmit}
                        onCopyText={handleCopyText}
                        onSaveReport={handleSaveReport}
                        setFeedback={setFeedback}
                        onResponse={handleSubmit}
                        setReport={setReport}

                    />
                ) : (
                    <PromptSuggestions
                        suggestions={promptSuggestions}
                        onSuggestionClick={(prompt) => {
                            void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.analysisSuggestionClick);
                            submitFromSuggestionRef.current = true;
                            void handleSubmit(prompt);
                        }}
                    />
                )}
                <div ref={reportEndRef} />
            </div>

            <div className='border-t border-gray-200 rounded-t-xl pt-1 absolute fixed bottom-0 left-0 right-0 mr-2 ml-2 bg-white'>
                <SelectedItemsDisplay
                    selectedFile={fileUploadProps.selectedFile}
                    selectedMaps={fileUploadProps.selectedMaps}
                    swimlanes={swimLane}
                    onRemoveFile={fileUploadProps.removeFile}
                    onRemoveMap={fileUploadProps.removeMap}
                    className="mx-4 mb-1"
                />

                <PromptInput
                    prompt={prompt}
                    setPrompt={setPrompt}
                    isLoading={isLoading}
                    onSubmit={handleSubmit}
                    swimlanes={swimLane}
                    fileUploadProps={fileUploadProps}
                    isDisableInput={isDisableInput}
                />
            </div>



            <SaveAnalysisModal
                isOpen={isSaveModalOpen}
                onClose={() => setIsSaveModalOpen(false)}
                onSave={handleSaveAndNew}
            />

            <ReportsModal
                isOpen={isReportsModalOpen}
                onClose={() => setIsReportsModalOpen(false)}
                onReportSelect={handleReportSelect}
                mapId={mapId}
                accessToken={users.access_token}
                isAnalyseSaved={isAnalyseSaved}
            />

            {/* Modal crédits insuffisants */}
            <span ref={insufficientCreditsModalRef} data-bs-target={`#insufficientCreditsModalAnalysisPanel`} data-bs-toggle='modal'></span>
            {insufficientCreditsInfo && (
                <InsufficientCreditsModal
                    id="insufficientCreditsModalAnalysisPanel"
                    creditsRequired={insufficientCreditsInfo.creditsRequired}
                    creditsAvailable={insufficientCreditsInfo.creditsAvailable}
                    deficit={insufficientCreditsInfo.deficit}
                    plan={insufficientCreditsInfo.plan}
                    onClose={() => setInsufficientCreditsInfo(null)}
                    isOwner={isOwner}
                    adminEmail={ownerEmail}
                />
            )}
        </>
    );
}

export default AnalysisPanel;